import { logger } from './logger';
import { llmUsageLogger } from './llm-usage-logger';

/**
 * Chat Analytics Client
 * Posts analytics events to the global SA API after each user prompt request.
 *
 * Required env vars:
 *   SA_API_URL    — Base URL of the SA API (e.g. https://api.superatom.ai)
 *
 * Optional:
 *   SA_ORG_ID              — Organization ID (nullable, for multi-tenant filtering)
 *   SA_ANALYTICS_ENABLED   — Set to 'false' to disable (default: true)
 */

export interface ChatAnalyticsEvent {
  userId: string;
  orgId?: string;
  projectId: string;
  threadId: string;
  messageIndex: number;
  question: string;
  sourcesUsed?: { sourceId: string; sourceName: string; sourceType: string }[];
  sqlGenerated?: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cost: string;
  latencyMs: number;
  status: 'success' | 'error';
  errorMessage?: string;
}

class AnalyticsClient {
  private apiUrl: string | null = null;
  private orgId: string | null = null;
  private enabled: boolean = true;
  private initialized: boolean = false;

  private init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.apiUrl = process.env.SA_API_URL || null;
    this.orgId = process.env.SA_ORG_ID || null;
    this.enabled = process.env.SA_ANALYTICS_ENABLED !== 'false';

    if (!this.apiUrl) {
      if (this.enabled) {
        logger.warn('[Analytics] Disabled — SA_API_URL not configured');
      }
      this.enabled = false;
    }
  }

  /**
   * Track a chat analytics event. Fire-and-forget — never blocks or throws.
   */
  async track(event: ChatAnalyticsEvent): Promise<void> {
    this.init();
    if (!this.enabled) return;

    try {
      const response = await fetch(`${this.apiUrl}/analytics/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'unknown');
        logger.warn(`[Analytics] Failed to track event: ${response.status} ${errorText}`);
      }
    } catch (err) {
      logger.warn(`[Analytics] Failed to send event: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  /**
   * Build a ChatAnalyticsEvent from the available request context.
   * Reads session stats from llmUsageLogger automatically.
   */
  buildEvent(params: {
    userId: string;
    projectId: string;
    threadId: string;
    messageIndex: number;
    question: string;
    success: boolean;
    latencyMs: number;
    model: string;
    errorMessage?: string;
    sourcesUsed?: { sourceId: string; sourceName: string; sourceType: string }[];
    sqlGenerated?: string;
  }): ChatAnalyticsEvent {
    this.init();

    const stats = llmUsageLogger.getSessionStats();

    return {
      userId: params.userId,
      orgId: this.orgId || undefined,
      projectId: params.projectId,
      threadId: params.threadId,
      messageIndex: params.messageIndex,
      question: params.question,
      sourcesUsed: params.sourcesUsed,
      sqlGenerated: params.sqlGenerated,
      model: params.model,
      inputTokens: stats.totalInputTokens,
      outputTokens: stats.totalOutputTokens,
      cost: stats.totalCostUSD.toFixed(6),
      latencyMs: params.latencyMs,
      status: params.success ? 'success' : 'error',
      errorMessage: params.errorMessage,
    };
  }
}

export const analyticsClient = new AnalyticsClient();
